<template>
  <div class="choose-city-step">
    <div class="choose-city-shell">
      <div
        class="choose-city-container"
        aria-live="polite"
        :aria-busy="isLoading"
      >
        <!-- Скелеты во время загрузки -->
        <div
          v-if="isLoading"
          class="cities-skeleton"
        >
          <div
            v-for="regionIndex in 5"
            :key="regionIndex"
            class="skeleton-region"
          >
            <div class="skeleton-region-header">
              <div class="skeleton skeleton-flag"></div>

              <div
                class="skeleton skeleton-region-title"
                :class="{
                  'skeleton-region-title-short': regionIndex % 2 === 0
                }"
              ></div>
            </div>

            <div class="skeleton-city-list">
              <div
                v-for="cityIndex in getSkeletonCitiesCount(regionIndex)"
                :key="cityIndex"
                class="skeleton skeleton-city"
                :class="{
                  'skeleton-city-short': cityIndex % 3 === 0
                }"
              ></div>
            </div>
          </div>
        </div>

        <!-- Текущие ячейки RegionItem оставляем без изменений -->
        <div
          v-else
          class="cities-list"
        >
          <RegionItem
            v-for="(regionCities, regionName) in groupedCities"
            :key="regionName"
            :regionName="regionName"
            :flag="regionCities[0]?.flag"
            :cities="regionCities"
            :selectedCityId="selectedCity?._id"
            @select-city="selectCity"
          />
        </div>

        <!-- Ошибка загрузки -->
        <div
          v-if="!isLoading && loadError"
          class="load-error"
        >
          <p class="load-error-title">
            Не удалось загрузить города
          </p>

          <p class="load-error-description">
            Проверьте подключение и попробуйте ещё раз.
          </p>

          <button
            class="retry-button"
            type="button"
            @click="loadCities"
          >
            Повторить
          </button>
        </div>
      </div>
    </div>
  </div>

  <teleport to="body">
    <div
      v-if="isSaving"
      class="global-busy-overlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="logout-spinner"></div>
      <p class="logout-text">Сохраняем город…</p>
    </div>
  </teleport>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref
} from "vue";

import RegionItem from "../RegionItem.vue";
import CitiesService from "../../api/CitiesService.js";
import UserService from "../../api/UserService.js";

const emit = defineEmits([
  "start-saving-city",
  "stop-saving-city",
  "save-city"
]);

const cities = ref([]);
const selectedCity = ref(null);

const isLoading = ref(true);
const isSaving = ref(false);
const loadError = ref(false);

const groupedCities = computed(() => {
  const groups = {};

  cities.value.forEach((city) => {
    const region =
      city.region_name || "Other";

    if (!groups[region]) {
      groups[region] = [];
    }

    groups[region].push(city);
  });

  return groups;
});

async function loadCities() {
  isLoading.value = true;
  loadError.value = false;

  try {
    const result =
      await CitiesService.fetchCities();

    cities.value =
      Array.isArray(result)
        ? result
        : [];
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки городов:",
      error
    );

    cities.value = [];
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function selectCity(city) {
  if (isSaving.value) return;

  selectedCity.value = city;
}

async function updateCity() {
  if (
    !selectedCity.value ||
    isSaving.value
  ) {
    return;
  }

  try {
    isSaving.value = true;
    emit("start-saving-city");

    const cityData = {
      id: selectedCity.value._id,
      name: selectedCity.value.city,
      region_id:
        selectedCity.value.region_id,
      region_name:
        selectedCity.value.region_name
    };

    await UserService.updateUserCity(cityData);

    emit("save-city");
  } catch (error) {
    console.error(
      "❌ Ошибка сохранения города:",
      error
    );
  } finally {
    isSaving.value = false;
    emit("stop-saving-city");
  }
}

function getSkeletonCitiesCount(index) {
  return index % 2 === 0 ? 3 : 4;
}

onMounted(() => {
  loadCities();
});

defineExpose({
  updateCity
});
</script>

<style scoped>
/*
 Внешний контейнер занимает оставшееся пространство шага.
 Он не зависит от количества регионов и городов.
*/
.choose-city-step {
  width: 600px;
  min-width: 0;

  display: flex;
  flex: 1;
  align-self: stretch;

  /*
   В Registration.vue:
   header + progress + описание шага + footer занимают часть viewport.
   Эта высота задаёт безопасный предел для списка.
  */
  min-height: 0;
  height: calc(100vh - 245px);
  max-height: calc(100vh - 245px);
}

/*
 Внутренний каркас всегда имеет размер внешнего контейнера.
 Количество элементов его не растягивает.
*/
.choose-city-shell {
  width: 500px;
  min-height: 0;
  height: 100%;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  padding: 10px;

  overflow: hidden;

  border-radius: 12px;

  background-color:
    rgba(93, 93, 93, 0.07);
}

/*
 Скролл только здесь.
 Сам контейнер всегда остаётся в пределах shell.
*/
.choose-city-container {
  position: relative;

  width: 100%;
  min-height: 0;
  height: 100%;

  display: flex;
  flex-direction: column;

  overflow: hidden;
}

/*
 Именно список скроллится.
 Его содержимое не может изменить высоту родителя.
*/
.cities-list {
  width: 100%;
  min-height: 0;
  flex: 1;

  display: flex;
  flex-direction: column;
  gap: 20px;

  box-sizing: border-box;
  padding-right: 10px;

  overflow-x: hidden;
  overflow-y: auto;

  scrollbar-width: none;
  -ms-overflow-style: none;
}

.cities-list::-webkit-scrollbar {
  display: none;
}

/* Skeleton */

.cities-skeleton {
  width: 100%;
  min-height: 0;
  flex: 1;

  display: flex;
  flex-direction: column;
  gap: 28px;

  box-sizing: border-box;
  padding-right: 10px;

  overflow: hidden;
}

.skeleton-region {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-region-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.skeleton-city-list {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.skeleton {
  position: relative;
  overflow: hidden;

  background:
    var(--choose-city-skeleton-bg);

  border-radius: 10px;
}

.skeleton::after {
  position: absolute;
  inset: 0;

  content: "";

  background:
    linear-gradient(
      90deg,
      transparent 0%,
      var(--choose-city-shimmer) 50%,
      transparent 100%
    );

  transform: translateX(-100%);

  animation:
    chooseCityShimmer 1.35s
    ease-in-out infinite;
}

.skeleton-flag {
  width: 26px;
  height: 18px;

  flex-shrink: 0;

  border-radius: 3px;
}

.skeleton-region-title {
  width: 145px;
  height: 17px;
}

.skeleton-region-title-short {
  width: 105px;
}

.skeleton-city {
  width: 100%;
  height: 43px;

  border-radius: 12px;
}

.skeleton-city-short {
  width: 82%;
}

@keyframes chooseCityShimmer {
  to {
    transform: translateX(100%);
  }
}

/* Error */

.load-error {
  min-height: 0;
  flex: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  padding: 30px 20px;

  text-align: center;
}

.load-error-title {
  margin: 0;

  color:
    var(--header-text-dark);

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 15px;
}

[data-theme="light"] .load-error-title {
  color:
    var(--header-text-light);
}

.load-error-description {
  max-width: 270px;

  margin: 8px 0 0;

  color:
    var(--choose-city-error-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 12px;
  line-height: 1.45;
}

.retry-button {
  margin-top: 17px;
  padding: 10px 22px;

  border: none;
  border-radius: 24px;

  background: #2887f7;
  color: #ffffff;

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 12px;

  cursor: pointer;
}

/* Save overlay */

.global-busy-overlay {
  position: fixed;
  inset: 0;

  z-index: 100000;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  background:
    rgba(0, 0, 0, 0.8);

  animation:
    fadeIn 0.3s forwards;

  pointer-events: all;
}

.logout-spinner {
  width: 50px;
  height: 50px;

  border:
    5px solid
    rgba(255, 255, 255, 0.3);

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin 0.8s linear infinite;
}

.logout-text {
  margin-top: 15px;

  color: white;

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 16px;

  opacity: 0;

  animation:
    fadeIn 0.6s forwards 0.2s;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

/* Mobile */

@media (max-width: 768px) {
  .choose-city-step {
    width: 100%;
    max-width: 340px;

    /*
     На мобильном header регистрации переносится,
     поэтому доступной высоты немного меньше.
    */
    height: calc(100dvh - 300px);
    max-height: calc(100dvh - 300px);
    min-height: 360px;

    margin: 0 auto;
  }

  .choose-city-shell {
    padding: 12px;
  }

  .cities-list,
  .cities-skeleton {
    gap: 16px;
    padding-right: 0;
  }
}

@media (max-height: 720px) and (min-width: 769px) {
  .choose-city-step {
    height: calc(100vh - 220px);
    max-height: calc(100vh - 220px);
    min-height: 340px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton::after,
  .logout-spinner {
    animation-duration:
      0.01ms !important;
  }
}
</style>

<style>
:root {
  --choose-city-skeleton-bg:
    rgba(255, 255, 255, 0.055);

  --choose-city-shimmer:
    rgba(255, 255, 255, 0.09);

  --choose-city-error-muted:
    rgba(255, 255, 255, 0.58);
}

[data-theme="light"] {
  --choose-city-skeleton-bg:
    rgba(18, 38, 66, 0.065);

  --choose-city-shimmer:
    rgba(255, 255, 255, 0.7);

  --choose-city-error-muted:
    rgba(25, 27, 32, 0.58);
}
</style>